import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/categoryData';

const articleDetailImages = [
  'https://readdy.ai/api/search-image?query=Digital%20security%20shield%20protection%20concept%2C%20minimalist%20professional%20illustration%2C%20warm%20coral%20amber%20gradient%20background%2C%20clean%20modern%20aesthetic%2C%20abstract%20geometric%20patterns&width=1200&height=600&seq=article-detail-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=User%20login%20authentication%20security%20concept%2C%20minimalist%20digital%20key%20design%2C%20warm%20professional%20atmosphere%2C%20coral%20accents%20on%20neutral%20background&width=1200&height=600&seq=article-detail-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Official%20announcement%20document%20concept%2C%20minimalist%20megaphone%20and%20stamp%2C%20warm%20professional%20illustration%2C%20amber%20gold%20accents%20on%20clean%20background&width=1200&height=600&seq=article-detail-03&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Password%20recovery%20reset%20concept%20illustration%2C%20minimalist%20key%20symbol%2C%20warm%20professional%20design%2C%20coral%20color%20scheme%20on%20neutral%20background&width=1200&height=600&seq=article-detail-04&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Identity%20verification%20biometric%20security%20concept%2C%20abstract%20fingerprint%20scan%2C%20minimalist%20warm%20illustration%2C%20amber%20gold%20accents&width=1200&height=600&seq=article-detail-05&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Beginners%20guide%20tutorial%20learning%20concept%2C%20minimalist%20book%20illustration%2C%20warm%20soft%20lighting%2C%20coral%20accents%20on%20neutral%20background&width=1200&height=600&seq=article-detail-06&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Mobile%20phone%20digital%20access%20technology%20concept%2C%20minimalist%20illustration%2C%20warm%20amber%20coral%20color%20scheme%2C%20clean%20professional%20design&width=1200&height=600&seq=article-detail-07&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Fact%20checking%20rumor%20control%20concept%2C%20minimalist%20magnifying%20glass%20illustration%2C%20warm%20professional%20design%2C%20amber%20accents&width=1200&height=600&seq=article-detail-08&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Account%20security%20protection%20lock%20shield%20concept%2C%20abstract%20minimalist%20illustration%2C%20warm%20coral%20amber%20professional%20design&width=1200&height=600&seq=article-detail-09&orientation=landscape',
];

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article.slug, article.categorySlug, 3) : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-4">
              <i className="ri-file-unknow-line text-2xl text-secondary-500"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2 font-heading">文章不存在</h1>
            <p className="text-sm text-foreground-500 mb-4">该文章可能已被删除或链接地址有误</p>
            <Link to="/" className="text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleIndex = parseInt(article.id) - 1;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: { '@type': 'Organization', name: '91官网' },
    publisher: { '@type': 'Organization', name: '91官网', url: 'https://91guanwang.com/' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://91guanwang.com/article/${article.slug}` },
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: article.category, path: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">阅读时间 {article.readTime}</span>
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 font-heading leading-tight">
                {article.title}
              </h1>

              <div className="h-[300px] md:h-[400px] rounded-xl overflow-hidden mb-8">
                <img
                  src={articleDetailImages[articleIndex % articleDetailImages.length]}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="prose-custom">
                {article.content.map((paragraph, index) => (
                  <p key={index} className="text-base text-foreground-700 leading-relaxed mb-5">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500 mr-2 whitespace-nowrap">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-secondary-50 text-secondary-700 border border-secondary-200/50 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-10 pt-10 border-t border-background-200/70">
                <h3 className="text-xl font-bold text-foreground-950 mb-6 font-heading">相关文章推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                    >
                      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-3 whitespace-nowrap">
                        {rel.category}
                      </span>
                      <h4 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{rel.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}